import { TPifRiskRuleCode } from "../codecs/PifRiskRuleCode";

export type TPifRiskRulePresentation = {
    section: string,
    field_label: string,
    summary: string,
    advice: string,
};

export const pifRiskRulesPresentation: Record<TPifRiskRuleCode, TPifRiskRulePresentation> = {
    pif_neighbour_dispute: {
        section: "Disputes & Complaints",
        field_label: "Neighbour disputes",
        summary: "Active or historical neighbour dispute disclosed",
        advice: "Request full details, dates, and any correspondence. Check if dispute is ongoing or resolved. Consider whether indemnity insurance is needed.",
    },
    pif_legal_action: {
        section: "Disputes & Complaints",
        field_label: "Legal action",
        summary: "Legal action associated with the property",
        advice: "Obtain full details of litigation. Check court records. Assess whether this affects title or creates ongoing liability for buyer.",
    },
    pif_antisocial_behaviour: {
        section: "Disputes & Complaints",
        field_label: "Anti-social behaviour",
        summary: "Anti-social behaviour reported in the area",
        advice: "Request details and check local authority records. May affect property value and buyer satisfaction.",
    },
    pif_noise_issues: {
        section: "Disputes & Complaints",
        field_label: "Noise issues",
        summary: "Noise issues affecting the property",
        advice: "Request specifics (source, frequency). Check local authority noise complaints register.",
    },
    pif_extension_approval_missing: {
        section: "Alterations & Changes",
        field_label: "Extension approval",
        summary: "Extension built without planning permission or building regulations",
        advice: "High risk. Obtain retrospective approval or arrange indemnity insurance. May need regularisation certificate. Lender may refuse mortgage.",
    },
    pif_extension_approval_partial: {
        section: "Alterations & Changes",
        field_label: "Extension approval",
        summary: "Extension has only partial approvals",
        advice: "Missing approval needed. May need retrospective approval or indemnity insurance.",
    },
    pif_extension_approval_unknown: {
        section: "Alterations & Changes",
        field_label: "Extension approval",
        summary: "Extension approval status is unknown",
        advice: "Investigate urgently. Request any available documentation. Lender will likely require evidence of compliance.",
    },
    pif_loft_conversion: {
        section: "Alterations & Changes",
        field_label: "Loft conversion",
        summary: "Loft conversion carried out",
        advice: "Verify building regulations completion certificate exists. Check fire safety compliance (fire doors, escape route). Confirm structural calculations were done.",
    },
    pif_walls_removed: {
        section: "Alterations & Changes",
        field_label: "Internal walls removed",
        summary: "Internal walls removed",
        advice: "Confirm whether walls were load-bearing. If so, check structural engineer's certificate and building regulations sign-off.",
    },
    pif_change_of_use: {
        section: "Alterations & Changes",
        field_label: "Change of use",
        summary: "Change of use has occurred",
        advice: "Verify planning permission was obtained for the change. Check building regulations compliance.",
    },
    pif_planning_notices: {
        section: "Notices",
        field_label: "Planning notices",
        summary: "Planning notices received affecting property or area",
        advice: "Request copies of all notices. Check local planning portal for details of proposed development.",
    },
    pif_building_control_notices: {
        section: "Notices",
        field_label: "Building control notices",
        summary: "Building control notices received",
        advice: "Obtain full details immediately. May indicate safety or compliance failures. Could affect insurability and mortgage.",
    },
    pif_environmental_notices: {
        section: "Notices",
        field_label: "Environmental notices",
        summary: "Environmental notices received (contamination/flooding)",
        advice: "Obtain environmental search results. Check flood risk maps. May require specialist survey and affect insurance/mortgage.",
    },
    pif_japanese_knotweed: {
        section: "Specialist Issues",
        field_label: "Japanese Knotweed",
        summary: "Japanese Knotweed present or previously found",
        advice: "Major issue. Obtain professional treatment plan and insurance-backed guarantee. Most lenders require a management plan. Can cause structural damage.",
    },
    pif_flooding: {
        section: "Specialist Issues",
        field_label: "Flooding history",
        summary: "Property has previously flooded",
        advice: "Obtain detailed flood history. Check Environment Agency flood maps. Buildings insurance may be costly or have exclusions. Recommend flood risk assessment.",
    },
    pif_subsidence: {
        section: "Specialist Issues",
        field_label: "Subsidence / heave / landslip",
        summary: "Subsidence, heave, or landslip has affected the property",
        advice: "Obtain full structural engineer's report and any underpinning certificates. Check insurance claims history. Lender may require specialist survey.",
    },
    pif_asbestos: {
        section: "Specialist Issues",
        field_label: "Asbestos",
        summary: "Asbestos present in the property",
        advice: "Request asbestos survey report if available. Note location and condition. Safe if undisturbed but removal needed before any renovation work.",
    },
    pif_coastal_erosion: {
        section: "Specialist Issues",
        field_label: "Coastal erosion",
        summary: "Property at risk of coastal erosion",
        advice: "Major long-term risk. Check Environment Agency shoreline management plan. May affect property value and insurability. Lender may decline mortgage.",
    },
    pif_listed_building: {
        section: "Specialist Issues",
        field_label: "Listed / conservation",
        summary: "Property is listed or in a conservation area",
        advice: "Restrictions apply to alterations. Verify any past works had listed building consent. Ongoing maintenance obligations may apply.",
    },
    pif_insurance_claims: {
        section: "Insurance",
        field_label: "Insurance claims",
        summary: "Insurance claims made in the last 5 years",
        advice: "Request details of all claims. May indicate recurring issues (flooding, subsidence). Could affect future premiums.",
    },
    pif_insurance_refused: {
        section: "Insurance",
        field_label: "Insurance refused",
        summary: "Insurance has been refused, cancelled, or had special terms",
        advice: "Serious concern. Buyer may struggle to obtain buildings insurance. Investigate reasons and check current insurability before exchange.",
    },
    pif_boundary_disputes: {
        section: "Boundaries",
        field_label: "Boundary disputes",
        summary: "Boundary dispute exists",
        advice: "Obtain full details. May need mediation or legal resolution before sale. Check title plan against physical boundaries.",
    },
    pif_rights_of_way: {
        section: "Rights & Arrangements",
        field_label: "Rights of way",
        summary: "Rights of way exist across the property",
        advice: "Verify details and check title entries. Buyer should understand the practical impact on their use of the property.",
    },
    pif_informal_arrangements: {
        section: "Rights & Arrangements",
        field_label: "Informal arrangements",
        summary: "Informal arrangements with neighbours",
        advice: "These are not legally binding and may not transfer to buyer. Consider whether formal agreements should be put in place.",
    },
    pif_contamination: {
        section: "Environmental",
        field_label: "Contamination",
        summary: "Contamination reported on or near the property",
        advice: "Environmental search essential. May need specialist contamination survey. Could affect development potential and insurance.",
    },
    pif_pest_issues: {
        section: "Environmental",
        field_label: "Pest infestation",
        summary: "Pest infestation (woodworm, dry rot, etc.) reported",
        advice: "Check if treatment was carried out and obtain guarantee. May need specialist survey if issue is recent or ongoing.",
    },
    pif_damp_issues: {
        section: "Environmental",
        field_label: "Damp issues",
        summary: "Damp issues reported",
        advice: "Recommend damp survey. Check if treatment was carried out. Could indicate structural or drainage problems.",
    },
    pif_owners_disagree: {
        section: "Legal Ownership",
        field_label: "Owners agreement",
        summary: "Not all legal owners agree to the sale",
        advice: "Sale cannot proceed without all owners' consent. Urgent legal advice needed. May require court order if ownership is disputed.",
    },
    pif_title_restrictions: {
        section: "Legal Ownership",
        field_label: "Title restrictions",
        summary: "Title restrictions exist",
        advice: "Review official copies of title register. Restrictions may limit use or require consent for sale. May need restriction to be removed or complied with.",
    },
    pif_boundaries_dont_match_title: {
        section: "Legal Boundaries",
        field_label: "Boundaries vs title plan",
        summary: "Physical boundaries do not match the title plan",
        advice: "Significant issue. May indicate adverse possession, encroachment, or error. Will likely need resolution before sale can complete.",
    },
    pif_tenants_in_property: {
        section: "Occupiers",
        field_label: "Tenants",
        summary: "Tenants currently in the property",
        advice: "Obtain full tenancy details. Check notice periods and whether tenancy will be terminated before completion. May affect buyer's mortgage offer.",
    },
    pif_occupiers_wont_vacate: {
        section: "Occupiers",
        field_label: "Vacant possession",
        summary: "Not all occupiers will vacate by completion",
        advice: "Critical issue. All occupiers must sign a waiver of rights or vacate. Buyer's lender will almost certainly require vacant possession.",
    },
    pif_in_chain: {
        section: "Completion & Moving",
        field_label: "Property chain",
        summary: "Property is in a chain",
        advice: "Increases risk of delays and fall-throughs. Monitor chain progress closely. Consider break clauses.",
    },
    pif_dependent_purchase_searching: {
        section: "Completion & Moving",
        field_label: "Dependent purchase",
        summary: "Seller has dependent purchase but is still searching",
        advice: "High risk of delay. Sale may fall through if seller cannot find a property. Set timeline expectations.",
    },
    pif_dependent_purchase_offer_made: {
        section: "Completion & Moving",
        field_label: "Dependent purchase",
        summary: "Seller has dependent purchase with offer made but not yet accepted",
        advice: "Moderate risk. Monitor progress of seller's purchase. Consider timeline implications.",
    },
    pif_dependent_purchase_other: {
        section: "Completion & Moving",
        field_label: "Dependent purchase",
        summary: "Sale is dependent on seller purchasing another property",
        advice: "Track progress of the onward purchase. Align completion dates where possible.",
    },
    pif_short_lease: {
        section: "Additional Legal",
        field_label: "Lease term",
        summary: "Short lease remaining",
        advice: "Under 80 years is a serious concern (cost of extension increases sharply). Most lenders require 70+ years. Negotiate extension before exchange.",
    },
    pif_low_epc_rating: {
        section: "Energy",
        field_label: "EPC rating",
        summary: "Low EPC rating (F or G)",
        advice: "Poor energy efficiency. May not meet minimum energy standards for letting. Buyer should budget for energy improvements.",
    },
    pif_green_deal: {
        section: "Energy",
        field_label: "Green Deal plan",
        summary: "Green Deal plan attached to the property",
        advice: "Financial obligation transfers to new owner. Buyer must be informed before exchange. Check outstanding balance and repayment terms.",
    },
    pif_non_mains_drainage: {
        section: "Utilities & Services",
        field_label: "Sewerage",
        summary: "Non-mains drainage (septic tank, cesspit, or treatment plant)",
        advice: "Check compliance with Environment Agency regulations. May need General Binding Rules registration. Buyer should understand maintenance responsibilities.",
    },
    pif_services_crossing: {
        section: "Services Crossing",
        field_label: "Services crossing land",
        summary: "Property services cross neighbouring land",
        advice: "Check for formal easements. If informal, consider negotiating a formal right before sale.",
    },
    pif_commercial_use: {
        section: "Property Details",
        field_label: "Commercial use",
        summary: "Property includes commercial use",
        advice: "Check planning permission for mixed use. May affect residential mortgage. Different insurance requirements may apply.",
    },
    pif_spray_foam_insulation: {
        section: "Construction & Building Safety",
        field_label: "Spray foam insulation",
        summary: "Spray foam insulation has been installed at the property",
        advice: "Major mortgage red flag. Many lenders now refuse to lend on properties with spray foam insulation. Obtain the installer's handover pack and any material compliance certificate. Buyer may need a specialist survey before mortgage application.",
    },
    pif_building_safety_remediation_required: {
        section: "Construction & Building Safety",
        field_label: "Building safety remediation",
        summary: "Remediation is required to the property for building-safety reasons",
        advice: "Critical disclosure. Obtain the full remediation scope, costs and timeline. May affect insurability, mortgage availability and value. Check whether the developer or freeholder is responsible.",
    },
    pif_mines_and_minerals: {
        section: "Rights & Arrangements",
        field_label: "Mines and minerals",
        summary: "Mines and minerals under the property are owned separately",
        advice: "Significant title issue. Could give a third party rights to extract beneath the property. Check title register and consider indemnity insurance.",
    },
    pif_chancel_liability: {
        section: "Rights & Arrangements",
        field_label: "Chancel liability",
        summary: "Property is subject to chancel repair liability",
        advice: "Potentially expensive open-ended obligation to contribute to local church chancel repairs. Indemnity insurance is the usual remedy and is typically cheap.",
    },
    pif_party_wall_notice: {
        section: "Notices",
        field_label: "Party Wall notice",
        summary: "Notice under the Party Wall etc. Act 1996 has been received",
        advice: "Obtain copies of all party wall notices and any party wall award. Check whether works are ongoing and whether any liabilities transfer to buyer.",
    },
    pif_planning_or_buildreg_breach: {
        section: "Alterations & Changes",
        field_label: "Planning / building regs breach",
        summary: "Known breach of planning permission or building regulations, or unresolved control issue",
        advice: "Major risk. Obtain full details. May need retrospective approval, regularisation certificate or indemnity insurance. Lender may decline mortgage until resolved.",
    },
    pif_failed_transactions: {
        section: "Other Issues",
        field_label: "Failed transactions",
        summary: "Failed purchase transactions on the property in the last 12 months",
        advice: "Find out why prior sales fell through. Could indicate undisclosed issues a survey or search would uncover. Worth asking for the prior buyer's survey report if available.",
    },
    pif_crime_or_death_at_property: {
        section: "Other Issues",
        field_label: "Crime or death at property",
        summary: "Property has been subject to crime, burglary or violent death",
        advice: "Material disclosure under CPR 2008. Buyer should understand the nature, date and any ongoing impact on the property or area.",
    },
    pif_authority_notified_safety_issue: {
        section: "Specialist Issues",
        field_label: "Authority-notified safety issue",
        summary: "Authority or qualified expert has notified of an ongoing health or safety issue",
        advice: "Critical. Obtain copies of any notices, reports or correspondence. May indicate cladding, fire safety, structural or environmental concerns that affect mortgage and insurance.",
    },
    pif_council_tax_band_change_pending: {
        section: "Council Tax",
        field_label: "Council Tax band change pending",
        summary: "Alterations or vacancy may trigger a Council Tax band change or Empty Property Premium",
        advice: "Buyer should budget for a possible band reassessment. Check with the Valuation Office Agency and local authority for any pending review.",
    },
};
